import otisAiTrustHero from "@/assets/projects/otis-ai-trust-hero.jpg";
import kashaPersonalizationHero from "@/assets/projects/kasha-personalization-hero.jpg";
import kashaHeroMockup from "@/assets/projects/kasha-hero-mockup.jpg";
import otisMobileMockup from "@/assets/projects/otis-mobile-mockup.jpg";
import otisAnimation from "@/assets/otis-animation.json";
import projectKasha from "@/assets/project-kasha.jpg";
import project2 from "@/assets/project-2.jpg";
import alekbgHeroAsset from "@/assets/projects/alekbg-case-study/alekbg-live-home-desktop.png";
import openTagHeroAsset from "@/assets/projects/opentag-case-study/opentag-marketplace.webp";
import wellmaxiHeroAsset from "@/assets/projects/wellmaxi-case-study/wellmaxi-hero.png";



export type Project = {
  id: string;
  /** Whether this item belongs to the curated public case-study set. */
  isPublic?: boolean;
  title: string;
  title_bg: string;
  company: string;
  role: string;
  role_bg: string;
  timeline?: string;
  heroImage: string;
  mockupImage: string | null;
  animation?: unknown;
  context: string;
  context_bg: string;
  challenge: string;
  challenge_bg: string;
  aiElement: string;
  aiElement_bg: string;
  myRole: string[];
  myRole_bg: string[];
  process: string[];
  process_bg: string[];
  outcome: { metrics: string; metrics_bg: string; learning: string; learning_bg: string; impact: string; impact_bg: string };
  reflection: string;
  reflection_bg: string;
  tags: string[];
};

export const projects: Project[] = [
  {
    id: "otis-ai-marketing",
    isPublic: true,
    title: "Designing Trust for AI-Powered Marketing Automation",
    title_bg: "Проектиране на доверие в AI маркетинг автоматизация",
    company: "Otis AI",
    role: "Head of UX/UI & Business Analyst",
    role_bg: "Ръководител UX/UI и бизнес анализатор",
    timeline: "2023 – 2025",
    heroImage: otisAiTrustHero,
    mockupImage: otisMobileMockup,
    animation: otisAnimation,
    context: "Otis needed to make AI-assisted campaign planning understandable and usable for businesses managing digital advertising.",
    context_bg: "Otis трябваше да направи AI-подпомогнатото планиране на кампании разбираемо и използваемо за бизнеси, управляващи дигитална реклама.",
    challenge: "How do we design transparency into a 'black box' AI system while keeping the interface simple enough for non-technical small business owners?",
    challenge_bg: "Как да проектираме прозрачност в 'черна кутия' AI система, запазвайки интерфейса достатъчно прост за нетехнически собственици на малки бизнеси?",
    aiElement: "Predictive analytics, intelligent recommendations, automated campaign generation",
    aiElement_bg: "Предсказателни анализи, интелигентни препоръки, автоматизирано генериране на кампании",
    myRole: [
      "Led end-to-end UX strategy for AI-driven features",
      "Framed product questions around campaign setup, recommendations and user control",
      "Collaborated with ML engineers to translate model outputs into human-centered patterns",
    ],
    myRole_bg: [
      "Водех цялостната UX стратегия за AI функционалности",
      "Рамкирах продуктови въпроси около настройката на кампании, препоръките и потребителския контрол",
      "Работех с ML инженери за превръщане на резултатите от моделите в ориентирани към човека модели",
    ],
    process: [
      "Mapped campaign creation, recommendation and review flows",
      "Designed explanations that give recommendations useful context",
      "Created controls that let users review and adjust automated choices",
      "Used progressive disclosure to balance guidance with detail",
    ],
    process_bg: [
      "Картографирах потоците за създаване, препоръки и преглед на кампании",
      "Проектирах обяснения, които дават полезен контекст на препоръките",
      "Създадох контроли за преглед и корекция на автоматизираните избори",
      "Използвах постепенно разкриване за баланс между насоки и детайли",
    ],
    outcome: {
      metrics: "",
      metrics_bg: "",
      learning: "Users didn't need to understand the algorithm — they needed to understand the recommendation's basis",
      learning_bg: "Потребителите не трябваше да разбират алгоритъма — а основанието за препоръката",
      impact: "",
      impact_bg: "",
    },
    reflection: "",
    reflection_bg: "",
    tags: ["AI UX", "Trust Design", "Explainability", "B2B SaaS", "User Research"],
  },
  {
    id: "kasha-ai-personalization",
    title: "Designing Kasha.io: An End-to-End Business Platform",
    title_bg: "Дизайн на Kasha.io: цялостна бизнес платформа",
    company: "Kasha.io",
    role: "Head of Creative & UX/UI Design",
    role_bg: "Ръководител Creative & UX/UI Design",
    heroImage: kashaHeroMockup,
    mockupImage: null,
    context: "Kasha.io brings business formation, onboarding, bookings, billing, accounts, invoicing, website creation and internal tooling into one platform for entrepreneurs and small businesses.",
    context_bg: "Kasha.io обединява създаване на фирма, onboarding, резервации, плащания, бизнес сметки, фактуриране, създаване на уебсайт и вътрешни инструменти в една платформа за предприемачи и малки бизнеси.",
    challenge: "Make a wide set of business capabilities feel coherent, progressively understandable and connected across customer-facing and operational workflows.",
    challenge_bg: "Широк набор от бизнес възможности да се усеща последователен, постепенно разбираем и свързан между клиентските и оперативните потоци.",
    aiElement: "AI-assisted website creation within a broader connected business platform",
    aiElement_bg: "AI-асистирано създаване на уебсайт в рамките на по-широка свързана бизнес платформа",
    myRole: [
      "Owned product design from strategy and research framing through UX architecture and UI craft",
      "Designed connected user flows, interactions, prototypes and reusable system patterns",
      "Collaborated with product and engineering through handoff and iteration",
    ],
    myRole_bg: [
      "Поех продуктовия дизайн от стратегията и рамката на проучването до UX архитектурата и UI изработката",
      "Проектирах свързани потребителски потоци, интеракции, прототипи и преизползваеми системни модели",
      "Работех с продукт и разработка по предаването и итерациите",
    ],
    process: [
      "Framed the product and user context across a multi-module platform",
      "Structured progressive onboarding and connected operational workflows",
      "Designed interaction and visual patterns across product, marketing and internal tools",
      "Created reusable system foundations for consistent implementation",
      "Collaborated through prototyping, handoff and continued iteration",
    ],
    process_bg: [
      "Рамкиране на продуктовия и потребителския контекст в платформа с множество модули",
      "Структуриране на постепенен onboarding и свързани оперативни потоци",
      "Дизайн на интеракционни и визуални модели за продуктови, маркетинг и вътрешни инструменти",
      "Създаване на преизползваеми системни основи за последователна реализация",
      "Сътрудничество чрез прототипиране, предаване и последващи итерации",
    ],
    outcome: {
      metrics: "",
      metrics_bg: "",
      learning: "A broad platform becomes easier to understand when complexity is introduced progressively and every module shares a recognizable logic.",
      learning_bg: "Широка платформа става по-разбираема, когато сложността се въвежда постепенно и всеки модул споделя разпознаваема логика.",
      impact: "",
      impact_bg: "",
    },
    reflection: "",
    reflection_bg: "",
    tags: ["Product Strategy", "UX Architecture", "UI Design", "Design Systems", "B2B Platform"],
  },
  {
    id: "alekbg-commerce-brand",
    isPublic: true,
    title: "ALEKBG — Building a Brand and B2C/B2B Commerce Experience",
    title_bg: "ALEKBG — изграждане на бранд и B2C/B2B commerce изживяване",
    company: "ALEKBG",
    role: "End-to-End Product, UX/UI & Brand Design",
    role_bg: "Продуктов, UX/UI и бранд дизайн от край до край",
    heroImage: alekbgHeroAsset,
    mockupImage: null,
    context: "A shipped brand and multilingual commerce ecosystem serving retail customers, wholesale buyers and distributors across product discovery, configuration, purchase and quote-oriented journeys.",
    context_bg: "Реализирана бранд и многоезична commerce екосистема за retail клиенти, wholesale купувачи и дистрибутори — от откриването и конфигурацията до покупка и запитване за оферта.",
    challenge: "Connect brand identity, product education, decision support and operational commerce for two audiences without making either journey feel secondary.",
    challenge_bg: "Свързване на бранд идентичност, продуктово образование, подкрепа за решения и оперативна търговия за две аудитории, без нито един път да остава второстепенен.",
    aiElement: "A connected B2C and B2B commerce experience supported by calculators, product configuration, localization and quote-oriented professional flows.",
    aiElement_bg: "Свързано B2C и B2B commerce изживяване с калкулатори, продуктова конфигурация, локализация и професионални потоци за оферта.",
    myRole: ["Framed retail and wholesale product needs", "Designed IA, journeys, interaction and visual UI", "Extended the brand across physical and digital touchpoints", "Collaborated through prototyping, handoff, implementation and iteration"],
    myRole_bg: ["Рамкиране на retail и wholesale продуктовите нужди", "Дизайн на IA, пътувания, интеракции и визуален UI", "Развитие на бранда във физически и дигитални точки", "Сътрудничество чрез прототипиране, предаване, реализация и итерации"],
    process: ["Worked from customer, business and catalogue context", "Structured progressive product discovery and decision support", "Separated retail purchase from wholesale quote needs", "Created a visual system across brand, commerce and collateral", "Iterated from design concepts into the current shipped experience"],
    process_bg: ["Работа от клиентския, бизнес и каталожния контекст", "Структуриране на постепенно откриване и подкрепа за решенията", "Разделяне на retail покупката от wholesale нуждите за оферта", "Визуална система за бранд, commerce и материали", "Итерации от дизайн концепции до текущото реализирано изживяване"],
    outcome: { metrics: "", metrics_bg: "", impact: "", impact_bg: "", learning: "B2C and B2B can share one brand foundation while giving each audience the information and next steps its decisions require.", learning_bg: "B2C и B2B могат да споделят една бранд основа, когато всяка аудитория получава информацията и следващите стъпки, нужни за нейните решения." },
    reflection: "",
    reflection_bg: "",
    tags: ["Brand Strategy", "Product Design", "E-commerce", "B2B UX", "Multilingual"],
  },
  {
    id: "opentag-loyalty-system",
    isPublic: true,
    title: "OpenTag — Designing a Dual-Sided Loyalty System",
    title_bg: "OpenTag — дизайн на двустранна loyalty система",
    company: "OpenTag",
    role: "Senior UX/UI Designer",
    role_bg: "Senior UX/UI Designer",
    heroImage: openTagHeroAsset,
    mockupImage: null,
    context: "A dual-sided loyalty product connecting player progression and rewards with operator configuration and control.",
    context_bg: "Двустранен loyalty продукт, който свързва player прогреса и наградите с operator конфигурацията и контрола.",
    challenge: "Create one coherent system for two audiences with different goals: understandable value for players and flexible programme management for operators.",
    challenge_bg: "Една последователна система за две аудитории с различни цели: разбираема стойност за играчите и гъвкаво управление за операторите.",
    aiElement: "Connected loyalty architecture across operator configuration, earning, levels, rewards, activity and player profiles.",
    aiElement_bg: "Свързана loyalty архитектура за operator конфигурация, печелене, нива, награди, активност и player профили.",
    myRole: ["Product discovery and research framing", "Competitive analysis and product strategy", "Information architecture and journeys", "Interaction and UI design", "Prototyping, iteration and handoff"],
    myRole_bg: ["Продуктово откриване и рамкиране на проучването", "Конкурентен анализ и продуктова стратегия", "Информационна архитектура и journeys", "Интеракционен и UI дизайн", "Прототипиране, итерации и предаване"],
    process: ["Frame the player and operator relationship", "Analyse category patterns and existing product context", "Map the loyalty architecture and connected journeys", "Develop operator configuration and player-facing interactions", "Iterate from exploration to high-fidelity UI and handoff"],
    process_bg: ["Рамкиране на връзката player–operator", "Анализ на категорийни модели и съществуващ продуктов контекст", "Картографиране на loyalty архитектурата и свързаните journeys", "Развитие на operator конфигурация и player интеракции", "Итерации от проучване до high-fidelity UI и предаване"],
    outcome: { metrics: "", metrics_bg: "", impact: "", impact_bg: "", learning: "Player motivation and operator control become coherent when configuration has a visible relationship to progression and rewards.", learning_bg: "Player мотивацията и operator контролът стават последователни, когато конфигурацията има видима връзка с прогреса и наградите." },
    reflection: "",
    reflection_bg: "",
    tags: ["Product Discovery", "Loyalty UX", "Operator Tools", "Player Experience", "UI Design"],
  },
  {
    id: "wellmaxi-brand-catalogue",
    isPublic: true,
    title: "Wellmaxi — Brand and Digital Catalogue Design",
    title_bg: "Wellmaxi — бранд и дизайн на дигитален каталог",
    company: "Wellmaxi",
    role: "Brand, UX/UI & Digital Catalogue Design",
    role_bg: "Brand, UX/UI и дизайн на дигитален каталог",
    heroImage: wellmaxiHeroAsset,
    mockupImage: null,
    context: "A brand and catalogue experience for professional buyers comparing a broad range of technical kitchen and catering equipment.",
    context_bg: "Бранд и каталожно изживяване за професионални купувачи, които сравняват широка гама техническо кухненско и кетъринг оборудване.",
    challenge: "Connect a recognizable identity with category-first discovery, technical product information and clear paths toward enquiry.",
    challenge_bg: "Свързване на разпознаваема идентичност с откриване по категории, техническа продуктова информация и ясни пътища към запитване.",
    aiElement: "A connected brand, catalogue and enquiry experience across responsive digital and physical touchpoints.",
    aiElement_bg: "Свързано бранд, каталожно и enquiry изживяване в адаптивни дигитални и физически точки.",
    myRole: ["Brand strategy and visual identity", "Research framing and information architecture", "Catalogue and product-detail UX", "Responsive UI and design-system thinking", "Handoff across digital and brand applications"],
    myRole_bg: ["Бранд стратегия и визуална идентичност", "Рамкиране на проучването и информационна архитектура", "UX на каталог и продуктов детайл", "Адаптивен UI и дизайн система", "Предаване за дигитални и бранд приложения"],
    process: ["Define the visual foundation and application rules", "Structure categories, catalogue and product information", "Design paths from discovery to detail and enquiry", "Apply reusable patterns across responsive surfaces", "Connect digital catalogue and physical brand contexts"],
    process_bg: ["Дефиниране на визуалната основа и правилата за приложение", "Структуриране на категории, каталог и продуктова информация", "Пътища от откриване към детайл и запитване", "Преизползваеми модели за адаптивни повърхности", "Свързване на дигиталния каталог и физическия бранд контекст"],
    outcome: { metrics: "", metrics_bg: "", impact: "", impact_bg: "", learning: "A technical catalogue feels more credible and approachable when brand expression and information hierarchy reinforce each other.", learning_bg: "Техническият каталог е по-достоверен и достъпен, когато бранд изразяването и информационната йерархия се подкрепят взаимно." },
    reflection: "",
    reflection_bg: "",
    tags: ["Brand Strategy", "Visual Identity", "Catalogue UX", "UI Design", "Responsive Design"],
  },
];

export const getProject = (id?: string) => projects.find((p) => p.id === id);

/** Case studies included in the curated public set. */
export const isProjectPublic = (id?: string) => Boolean(getProject(id)?.isPublic);

/** Curated Home "selected work" card. Routing/access comes from `projects` via caseStudyId. */
export type HomeCard = {
  id: string;
  /** id in `projects`; omitted = no case study yet ("case study on request"). */
  caseStudyId?: string;
  title: string;
  company: string;
  descKey: string;
  image: string;
  animation?: unknown;
  animationBg?: string;
  animationFit?: "slice" | "meet";
  tags: string[];
};

const fromCaseStudy = (id: string) => {
  const p = getProject(id);
  if (!p) throw new Error(`Unknown case study id: ${id}`);
  return { caseStudyId: p.id, company: p.company };
};

export const homeCards: HomeCard[] = [
  {
    id: "kasha-ai-personalization",
    ...fromCaseStudy("kasha-ai-personalization"),
    title: "Kasha.io — An End-to-End Business Platform",
    descKey: "project.kasha.desc",
    image: projectKasha,
    tags: ["Product Design", "UX Architecture", "Design Systems"],
  },
  {
    id: "otis-ai-marketing",
    ...fromCaseStudy("otis-ai-marketing"),
    title: "Meetotis.com The AI Digital Advertiser",
    descKey: "project.otis.desc",
    image: project2,
    animation: otisAnimation,
    animationBg: "#F2F1F3",
    animationFit: "slice",
    tags: ["B2B SaaS", "AI UX", "Dashboard"],
  },
  {
    id: "alekbg-commerce-brand",
    ...fromCaseStudy("alekbg-commerce-brand"),
    title: "ALEKBG — Brand & B2C/B2B Commerce",
    descKey: "project.alekbg.desc",
    image: alekbgHeroAsset,
    tags: ["Brand Strategy", "E-commerce", "B2B UX"],
  },
  {
    id: "opentag-loyalty-system",
    ...fromCaseStudy("opentag-loyalty-system"),
    title: "OpenTag — Dual-Sided Loyalty System",
    descKey: "project.opentag.desc",
    image: openTagHeroAsset,
    tags: ["Loyalty UX", "Operator Tools", "Product Design"],
  },
  {
    id: "wellmaxi-brand-catalogue",
    ...fromCaseStudy("wellmaxi-brand-catalogue"),
    title: "Wellmaxi — Brand & Digital Catalogue",
    descKey: "project.wellmaxi.desc",
    image: wellmaxiHeroAsset,
    tags: ["Brand Strategy", "Catalogue UX", "UI Design"],
  },
];
